import { AnyAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';

import { ApiError, ApiResponse } from '../../../../../base/api';
import { Loading } from '../../../../../base/enums';
import { appActions } from '../../../../../base/redux/slices/appSlice';
import { RootState } from '../../../../../base/redux/store';
import { isEmptyObject } from '../../../../../base/utils';
import { settingsApi } from '../../api';
import { ChangeLoginStep, ChangePasswordStep } from '../../enums';
import { settingsActions } from '../slice/settingsSlice';
import { __t } from '../../../../../base/utils/t';
import { profileActions } from '../slice/profileSlice';
import { mapApiProfileDataToState, mapProfileDataStateToApiParams } from '../../utils';
import { recruiterAppActions } from '../../../../redux/slices/recruiterAppSlice';
import { baseErrorHandler } from '../../../../../base/redux/sagas/appSaga';
import { GetLoginResponse, ProfileDataParams, ProfileLogoResponse } from '../../api/interfaces';

function* getCurrentLogin() {
    yield put(appActions.setLoading(Loading.App));

    try {
        const {
            data: { email },
        }: ApiResponse<GetLoginResponse> = yield call(settingsApi.getLogin);
        yield put(settingsActions.setCurrentLogin(email));
    } catch (e: unknown) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* changeLoginSubmit({ payload: callback }: AnyAction) {
    yield put(appActions.setLoading(Loading.App));
    const { fields, step } = yield select((state: RootState) => state.recruiterAppSettings.changeLogin);
    try {
        if (step === ChangeLoginStep.Password) {
            yield call(settingsApi.changeLogin, { password: fields.password, validate: true });
            yield put(settingsActions.setChangeLoginStep(ChangeLoginStep.NewLogin));
            return;
        }

        yield call(settingsApi.changeLogin, {
            password: fields.password,
            email: fields.email,
            emailCode: fields.emailCode,
        });

        yield put(
            appActions.enqueueSnackbar({
                id: uuid(),
                message: __t('Электронная почта успешно изменена'),
                icon: 'circle-check-solid',
                iconColor: 'success',
            }),
        );

        if (callback) {
            callback();
        }
    } catch (e: unknown) {
        if (e instanceof ApiError) {
            if (e.emailVerificationNeeded && step !== ChangeLoginStep.Confirmation) {
                yield put(settingsActions.setChangeLoginStep(ChangeLoginStep.Confirmation));
                return;
            }
            if (e.errors && !isEmptyObject(e.errors)) {
                yield put(settingsActions.setChangeLoginErrors(e.errors));
                yield put(settingsActions.setChangeLoginShouldScrollToError(true));
                return;
            }
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* changePasswordSubmit({ payload: callback }: AnyAction) {
    yield put(appActions.setLoading(Loading.App));
    const { fields, step } = yield select((state: RootState) => state.recruiterAppSettings.changePassword);

    try {
        if (step === ChangePasswordStep.OldPassword) {
            yield call(settingsApi.changePassword, { password: fields.password, validate: true });
            yield put(settingsActions.setChangePasswordStep(ChangePasswordStep.NewPassword));
            return;
        }

        yield call(settingsApi.changePassword, fields);

        yield put(
            appActions.enqueueSnackbar({
                id: uuid(),
                message: __t('Пароль успешно сохранен'),
                icon: 'circle-check-solid',
                iconColor: 'success',
            }),
        );

        if (callback) {
            callback();
        }
    } catch (e: unknown) {
        if (e instanceof ApiError && e.errors && !isEmptyObject(e.errors)) {
            yield put(settingsActions.setChangePasswordErrors(e.errors));
            yield put(settingsActions.setChangePasswordShouldScrollToError(true));
            return;
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* changeLoginSubmitChangeConfirmationValue({ payload: { newValue, recaptchaToken, callback } }: AnyAction) {
    yield put(appActions.setLoading(Loading.App));
    try {
        const { password } = yield select((state: RootState) => state.recruiterAppSettings.changeLogin.fields);
        yield call(settingsApi.changeLogin, { password, email: newValue, validate: true });
        yield put(settingsActions.setChangeLoginField({ id: 'email', value: newValue }));
        yield put(recruiterAppActions.sendConfirmationCode(newValue, recaptchaToken));
        callback();
    } catch (e: unknown) {
        if (e instanceof ApiError) {
            if (e.errors && !isEmptyObject(e.errors)) {
                yield put(settingsActions.setChangeLoginErrors(e.errors));
                yield put(settingsActions.setChangeLoginShouldScrollToError(true));
                return;
            }
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* gerProfile() {
    yield put(appActions.setLoading(Loading.App));
    const { countries, areas } = yield select((state: RootState) => state.recruiterApp.config);
    try {
        const { data }: ApiResponse<ProfileDataParams> = yield call(settingsApi.getProfileData);
        const preparedData = mapApiProfileDataToState(data, countries, areas);
        yield put(profileActions.setProfileData(preparedData));
    } catch (e: unknown) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* submitProfile({ payload: callback }: AnyAction) {
    yield put(appActions.setLoading(Loading.App));
    const { fields } = yield select((state: RootState) => state.recruiterAppProfile);
    try {
        yield call(settingsApi.submitProfile, mapProfileDataStateToApiParams(fields));
        if (callback) {
            callback();
        }
        yield put(
            appActions.enqueueSnackbar({
                id: uuid(),
                message: __t('Изменения успешно сохранены'),
                icon: 'circle-check-solid',
                iconColor: 'success',
            }),
        );
    } catch (e: unknown) {
        if (e instanceof ApiError && e.errors && !isEmptyObject(e.errors)) {
            yield put(profileActions.setErrors(e.errors));
            yield put(profileActions.setShouldScrollToError(true));
            return;
        }

        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* uploadProfileLogo({ payload: image }: AnyAction) {
    try {
        const { data }: ApiResponse<ProfileLogoResponse> = yield call(settingsApi.saveLogo, image);
        yield put(profileActions.setField({ id: 'image', value: data.image }));
        yield put(profileActions.setField({ id: 'imageUrl', value: data.imageUrl }));
    } catch (e: unknown) {
        if (e instanceof ApiError && e.errors && !isEmptyObject(e.errors)) {
            yield put(
                appActions.enqueueSnackbar({
                    id: uuid(),
                    message: e.errors.image[0],
                    title: image.name,
                    icon: 'triangle-exclamation-duonote-red',
                    iconColor: 'danger',
                }),
            );
        }
        throw new Error('Recruiter logo upload unsuccessful');
    }
}

export function* settingsSaga() {
    yield takeLatest(settingsActions.getCurrentLogin, getCurrentLogin);

    yield takeLatest(settingsActions.changeLoginSubmit, changeLoginSubmit);
    yield takeLatest(
        settingsActions.changeLoginSubmitChangeConfirmationValue,
        changeLoginSubmitChangeConfirmationValue,
    );
    yield takeLatest(settingsActions.changePasswordSubmit, changePasswordSubmit);

    yield takeLatest(profileActions.getProfileData, gerProfile);
    yield takeLatest(profileActions.submitProfile, submitProfile);
    yield takeLatest(profileActions.uploadLogo, uploadProfileLogo);
}
